import React from "react";
import { useContext } from "react";
import { SelectedPatientContext } from "../SelectedPatientContext.tsx";
import { Line } from "react-chartjs-2";
import {Card, CardHeader, Text} from "@fluentui/react-components";
import { webLightTheme } from '@fluentui/react-components';
import ChartStreaming from "@robloche/chartjs-plugin-streaming";
import { API_PATH } from "../Constants.ts";
import { Chart as ChartJS, LineElement, LinearScale, PointElement} from 'chart.js';
import {ControlPanelNavigation} from "./control-panel-navigation.js";
import {HeartPulse20Regular} from "@fluentui/react-icons";
ChartJS.register(ChartStreaming, LineElement, PointElement, LinearScale);
ChartJS.defaults.defaultFontFamily = "'Segoe UI', 'Segoe UI Web (West European)', -apple-system, BlinkMacSystemFont, Roboto, 'Helvetica Neue', sans-serifFont family fontFamilyBase";
ChartJS.defaults.defaultFontColor = webLightTheme.colorNeutralForeground1;
const isEmpty = array => Array.isArray(array) && array.length === 0;
const maxPacketIndexDiff = 3;

class Queue {
    _initialize(initialIndex) {
        this.items = [];
        this.index        = initialIndex;
        this.initialIndex = initialIndex;
        this.resetChartFlag = true;

        console.log('new queue initialized with content:');
        console.log('initialIndex: ' + initialIndex);
        console.log('index: ' + this.index);
    }

    constructor() {
        this._initialize(undefined);
    }

    enqueue(item) {
        if (isEmpty(this.items) || item.index < this.initialIndex - maxPacketIndexDiff) {
            console.log('resetting the queue');
            this._initialize(item.index);
        }

        this.items[item.index] = item;
    }

    hasNext() {
        while (this.index < this.items.length && this.items[this.index] === undefined)
            console.log('skipping undefined slot at index ' + this.index++);
        return this.index < this.items.length;
    }

    dequeue() {
        if (!this.hasNext()) return null;
        try     { return this.items[this.index];   }
        finally { delete this.items[this.index++]; }
    }

    resetChart() {
        return this.resetChartFlag ? (this.resetChartFlag = false, true) : false;
    }
}

export const BiofeedbackChart = () => {
    const { selectedPatientId} = useContext(SelectedPatientContext);
    const startDateRef = React.useRef(Date.now());
    const queueRef = React.useRef(new Queue());

    React.useEffect(() => {
        const timer = setInterval(() => {
            if (selectedPatientId===null) return;

            fetch(API_PATH.LOGS + `?limit=2&order[id]=desc&query=(tag=='VR'ANDtitle=='BiofeedbackData'ANDinserted_by_user_id==${encodeURIComponent(selectedPatientId)})&${Math.random()}`).then(
                result => {
                    return result.json();
                }).then(data => {
                    data.forEach(element => {
                        const message = JSON.parse(element.message);
                        queueRef.current.enqueue(message);
                    });
            });
        }, 1000);
        return () => clearInterval(timer);
    }, [selectedPatientId]);

    const data = {
        datasets: [
            {
                label: "Heart rate [bpm - beats per minute]",
                borderColor: "red",
                fill: false,
                lineTension: 0.25,
                borderWidth: 1,
                pointStyle: 'circle',
                data: []
            },
            {
                label: "Intensity",
                borderColor: "blue",
                fill: false,
                lineTension: 0.25,
                borderWidth: 1,
                pointStyle: 'circle',
                data: []
            }
        ]
    };

    const options = {
        plugins: {
            legend: {
                display: false
            },
            streaming: {
                frameRate: 15
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        elements: {
            line: {
                tension: 0.5
            }
        },
        scales: {
            x: {
                type: "realtime",
                realtime: {
                    onRefresh: function (chart) {
                        if (queueRef.current.resetChart()) {
                            console.log('resetting the chart');
                            startDateRef.current = Date.now();
                            chart.data.datasets.forEach(ds => ds.data = []);
                            chart.update();
                        }

                        while (queueRef.current.hasNext()) {
                            const front = queueRef.current.dequeue();
                            const packetNumber = Math.abs(front.index-queueRef.current.initialIndex);
                            console.log(`packet=${packetNumber} front=${front.index} queue=${queueRef.current.index}`);
                            console.log(`(${startDateRef.current + packetNumber*1000}, ${front.computedIntensity.toFixed(1)})`);

                            chart.data.datasets[0].data.push({
                                x: startDateRef.current + packetNumber*1000,
                                y: front.heartRate
                            });

                            chart.data.datasets[1].data.push({
                                x: startDateRef.current + packetNumber * 1000,
                                y: front.computedIntensity*50+50
                            });
                        }
                    },
                    delay: 1000,
                    duration: 120000
                }
            },
            y: {
                ticks: {
                    callback: function (value, index, values) {
                        return value + 'BPM';
                    }
                }
            }
        }
    };

    return (
        <>
            <div className='control-panel-layout'>
                <aside className="sidenav">
                    <ControlPanelNavigation/>
                </aside>
                <Card size='large' className="card" style={{ height: '88vh', margin: '12px' }}>
                    <CardHeader image={<HeartPulse20Regular />} header={<Text weight="semibold">Heart rate [bpm - beats per minute]</Text>} />
                    <Line data={data} options={options}/>
                </Card>
            </div>
        </>
    );
};